import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StepPhotos, StepDetails, StepLocation } from "@/components/SellSteps";

const STEPS = ["Photos", "Details", "Location & price"];

export default function Sell() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ images: [], title: "", description: "", category: "", condition: "good", price: "", is_free: false, state: user?.state || "QLD", suburb: user?.suburb || "", postcode: "" });
  const up = (patch) => setF((p) => ({ ...p, ...patch }));

  useEffect(() => {
    if (id) api.get(`/listings/${id}`).then(({ data }) => setF({ ...data, price: data.price || "", postcode: data.postcode || "" }));
  }, [id]);

  const valid = [f.images.length > 0, f.title.trim().length >= 3 && f.category, f.suburb.trim().length >= 2 && (f.is_free || Number(f.price) > 0)];

  const submit = async () => {
    setBusy(true);
    const body = { images: f.images, title: f.title, description: f.description, category: f.category, condition: f.condition, price: Number(f.price) || 0, is_free: f.is_free, state: f.state, suburb: f.suburb, postcode: f.postcode || null,
      era: f.category === "antiques" ? f.era || null : null, maker: f.category === "antiques" ? f.maker || null : null };
    try {
      const { data } = id ? await api.put(`/listings/${id}`, body) : await api.post("/listings", body);
      toast.success(id ? "Listing updated" : "Your item is live!");
      nav(`/listing/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const Body = [StepPhotos, StepDetails, StepLocation][step];

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">{id ? "Edit listing" : "Sell an item — free"}</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">3 quick steps and you're live.</h1>

      <ol className="mt-8 grid grid-cols-3 gap-2">
        {STEPS.map((s, i) => (
          <li key={s} data-testid={`sell-step-indicator-${i + 1}`} className="space-y-2">
            <div className={`h-1.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-[#d4f253]" : "bg-white/10"}`} />
            <p className={`flex items-center gap-1.5 text-xs font-semibold sm:text-sm ${i <= step ? "text-white" : "text-slate-500"}`}>
              {i < step ? <Check className="h-4 w-4 text-[#d4f253]" /> : <span>{i + 1}.</span>} {s}
            </p>
          </li>
        ))}
      </ol>

      <div key={step} className="rise mt-8 rounded-3xl bg-[#173742] p-6 sm:p-8">
        <Body f={f} up={up} />
      </div>

      <div className="mt-6 flex gap-3">
        {step > 0 && <button data-testid="sell-back-btn" onClick={() => setStep(step - 1)} className="rounded-xl border border-white/15 px-6 py-3.5 text-sm font-semibold hover:bg-white/5">Back</button>}
        {step < 2 ? (
          <button data-testid="sell-next-btn" disabled={!valid[step]} onClick={() => setStep(step + 1)} className="btn-lime flex-1 py-3.5 text-sm">Continue</button>
        ) : (
          <button data-testid="sell-publish-btn" disabled={!valid[2] || busy} onClick={submit} className="btn-lime flex-1 py-3.5 text-sm">{busy ? "Publishing…" : id ? "Save changes" : "Publish listing"}</button>
        )}
      </div>
      {step === 2 && <p data-testid="sell-terms-note" className="mt-4 text-center text-xs text-slate-400">By publishing you confirm you own this item, it's described honestly, and it isn't a <Link to="/terms#s6" className="text-[#d4f253] hover:underline">prohibited item</Link> under our <Link to="/terms" className="text-[#d4f253] hover:underline">Terms</Link>.</p>}
    </div>
  );
}
